<template>
  <!-- Ambient mesh background -->
  <div class="mesh-bg" aria-hidden="true">
    <div class="mesh-orb mesh-orb--1"></div>
    <div class="mesh-orb mesh-orb--2"></div>
    <div class="mesh-orb mesh-orb--3"></div>
  </div>

  <div class="app-layout">
    <TheNavbar />

    <main class="main-content" ref="mainRef" @scroll.passive="onMainScroll">
      <HeroSection />
      <AboutSection />
      <SkillsSection />
      <ResumeSection />
      <CertificationsSection />
      <TestimonialsSection />
      <ContactSection />
    </main>
  </div>

  <!-- Page indicator dots (right edge) -->
  <nav class="page-dots" aria-label="Page navigation">
    <button
      v-for="id in sections"
      :key="id"
      :class="['page-dot', { 'page-dot--active': activeSection === id }]"
      @click="navigateTo(id)"
      :aria-label="id"
    ></button>
  </nav>

  <!-- Back to start button (shows after leaving hero) -->
  <Transition name="fade-btop">
    <button v-if="showTop" class="back-to-top" @click="navigateTo('hero')" aria-label="Back to start">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
    </button>
  </Transition>
</template>

<script setup>
import { ref, provide, onMounted, onUnmounted } from 'vue'
import TheNavbar from './components/TheNavbar.vue'
import HeroSection from './components/HeroSection.vue'
import AboutSection from './components/AboutSection.vue'
import SkillsSection from './components/SkillsSection.vue'
import ResumeSection from './components/ResumeSection.vue'
import CertificationsSection from './components/CertificationsSection.vue'
import TestimonialsSection from './components/TestimonialsSection.vue'
import ContactSection from './components/ContactSection.vue'

const mainRef = ref(null)
const activeSection = ref('hero')
const showTop = ref(false)

const sections = ['hero', 'about', 'skills', 'resume', 'certifications', 'testimonials', 'contact']

function navigateTo(id) {
  const idx = sections.indexOf(id)
  if (idx < 0 || !mainRef.value) return
  mainRef.value.scrollTo({ left: idx * mainRef.value.clientWidth, behavior: 'smooth' })
}

function onMainScroll() {
  const m = mainRef.value
  if (!m) return
  const idx = Math.min(Math.round(m.scrollLeft / m.clientWidth), sections.length - 1)
  activeSection.value = sections[idx] ?? 'hero'
  showTop.value = m.scrollLeft > m.clientWidth * 0.3
}

provide('navigateTo', navigateTo)
provide('activeSection', activeSection)

function onKeyDown(e) {
  const m = mainRef.value
  if (!m) return
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    e.preventDefault()
    const idx = Math.min(Math.round(m.scrollLeft / m.clientWidth) + 1, sections.length - 1)
    navigateTo(sections[idx])
  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    e.preventDefault()
    const idx = Math.max(Math.round(m.scrollLeft / m.clientWidth) - 1, 0)
    navigateTo(sections[idx])
  }
}

onMounted(() => window.addEventListener('keydown', onKeyDown))
onUnmounted(() => window.removeEventListener('keydown', onKeyDown))
</script>

<style>
/* ── Page indicator dots ── */
.page-dots {
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 996;
}
.page-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
}
.page-dot--active {
  background: #00d4ff;
  height: 22px;
  border-radius: 3px;
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.5);
}
.page-dot:hover:not(.page-dot--active) {
  background: rgba(255, 255, 255, 0.5);
  transform: scale(1.3);
}

@media (max-width: 1024px) {
  .page-dots { right: 10px; gap: 8px; }
}

.back-to-top {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--color-gradient);
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 997;
  border: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 212, 255, 0.3);
}
.back-to-top:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.4);
}

.fade-btop-enter-active, .fade-btop-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-btop-enter-from, .fade-btop-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 1024px) {
  .back-to-top { bottom: 20px; right: 20px; }
}
</style>
